/**
 * Apply with Ari feature gate (TP-498).
 *
 * Apply with Ari runs as a Mida split URL A/B test (configured in Mida, not
 * here): the control is the plain job URL, the variant is the same URL with
 * `?apply=ari`. Only the variant URL shows the Apply with Ari box, on every
 * environment, so staging/local mirror production — test it there with
 * `?apply=ari`. The global toggle and override users in
 * featureFlagOverrideUserIds still switch it on for every job URL.
 */

import featureToggles from 'featureToggles';
import { FREE_USER_JOB_PITCH_LIMIT } from 'constants/constants';

/** Query param (`?apply=ari`) that marks the Mida variant job URL. */
export const APPLY_ARI_VARIANT_PARAM = 'apply';
export const APPLY_ARI_VARIANT_VALUE = 'ari';

/**
 * Whether a location search string is the Apply with Ari variant URL.
 *
 * @param {string|null|undefined} search - e.g. `location.search`
 * @returns {boolean}
 */
export function isApplyAriVariantSearch(search) {
  if (!search) return false;
  return new RegExp(
    `[?&]${APPLY_ARI_VARIANT_PARAM}=${APPLY_ARI_VARIANT_VALUE}(&|$)`
  ).test(search);
}

/**
 * Variant job page URL — used to send people back to the job without
 * dropping them out of the variant.
 *
 * @param {string|number} routeId - URL id (encoded slug or numeric)
 * @returns {string}
 */
export function getApplyAriVariantJobPath(routeId) {
  return `/projects/${routeId}?${APPLY_ARI_VARIANT_PARAM}=${APPLY_ARI_VARIANT_VALUE}`;
}

/**
 * Whether this user is allow-listed to exercise gated features on production
 * while the global toggle is still off.
 *
 * @param {{ id?: number }|null|undefined} user
 * @returns {boolean}
 */
export function isFeatureFlagOverrideUser(user) {
  if (!user || user.id == null) return false;
  const ids = featureToggles.featureFlagOverrideUserIds || [];
  return ids.includes(user.id);
}

/**
 * Whether Apply with Ari is enabled for this viewer.
 *
 * Rules (any one is enough):
 * - The viewer is on the Mida variant URL (`variant: true`)
 * - `featureToggles.applyWithAri` is on
 * - The user is in `featureFlagOverrideUserIds`
 *
 * Pass `user` when available (null/undefined for guests). Guests never match
 * the override list.
 *
 * @param {{ id?: number }|null|undefined} user
 * @param {{ variant?: boolean }} [opts]
 * @returns {boolean}
 */
export function isApplyWithAriEnabled(user, { variant = false } = {}) {
  return (
    Boolean(variant) ||
    !!featureToggles.applyWithAri ||
    isFeatureFlagOverrideUser(user)
  );
}

/**
 * Whether a logged-in user should get the Apply with Ari entry / chat
 * (TP-500). Uses the same enablement rules as guests (`isApplyWithAriEnabled`).
 * When false, CreativePitch / Easy Apply stay unchanged.
 *
 * @param {{ id?: number }|null|undefined} user
 * @param {{ variant?: boolean }} [opts]
 * @returns {boolean}
 */
export function canAccessApplyAriWhenLoggedIn(user, opts) {
  return Boolean(user && user.id) && isApplyWithAriEnabled(user, opts);
}

/**
 * Free daily application allowance exhausted (mirrors CreativePitch blocker).
 * When true, Apply with Ari shows the existing upgrade prompt — not a dead end.
 *
 * @param {{ id?: number, pro?: boolean, premium?: boolean, messages_count?: number }|null|undefined} user
 * @param {{ applicationCreditsRemaining?: number, briefNoPitchLimits?: boolean, authUserWasInvited?: boolean }} [opts]
 * @returns {boolean}
 */
export function isApplyAriDailyLimitReached(
  user,
  {
    applicationCreditsRemaining = 0,
    briefNoPitchLimits = false,
    authUserWasInvited = false,
  } = {}
) {
  if (!user || !user.id) return false;
  if (briefNoPitchLimits || authUserWasInvited || user.premium) return false;
  if (user.pro && Number(user.messages_count) > 0) return false;
  return (
    Number(user.messages_count) === 0 &&
    Number(applicationCreditsRemaining) <= 0
  );
}

/**
 * Popular-job limit reached (mirrors CreativePitch maximumPitchLimit): once a
 * job has more than FREE_USER_JOB_PITCH_LIMIT applications, users without
 * credits must upgrade. The LinkedIn landing-job bypass still applies.
 *
 * @param {{ id?: number, pro?: boolean, premium?: boolean, messages_count?: number }|null|undefined} user
 * @param {{ totalPitches?: number, applicationCreditsRemaining?: number, briefNoPitchLimits?: boolean, authUserWasInvited?: boolean, linkedInLandingJob?: boolean }} [opts]
 * @returns {boolean}
 */
export function isApplyAriJobPitchLimitReached(
  user,
  {
    totalPitches = 0,
    applicationCreditsRemaining = 0,
    briefNoPitchLimits = false,
    authUserWasInvited = false,
    linkedInLandingJob = false,
  } = {}
) {
  if (!user || !user.id) return false;
  if (briefNoPitchLimits || authUserWasInvited || user.premium) return false;
  if (user.pro && Number(user.messages_count) > 0) return false;
  if (linkedInLandingJob) return false;
  return (
    Number(totalPitches) > FREE_USER_JOB_PITCH_LIMIT &&
    Number(applicationCreditsRemaining) <= 0
  );
}

/**
 * What stops a signed-in user applying with Ari, in the same order as
 * CreativePitch `getPitchBlocker`, minus its bio check (the Ari flow asks for
 * a bio instead). Guests are never blocked here: their email is confirmed by
 * the passwordless code at the end of the chat.
 *
 * @param {{ id?: number, pro?: boolean, premium?: boolean, messages_count?: number, email_verified?: boolean }|null|undefined} user
 * @param {{ totalPitches?: number, applicationCreditsRemaining?: number, briefNoPitchLimits?: boolean, authUserWasInvited?: boolean, linkedInLandingJob?: boolean }} [opts]
 * @returns {'email-unverified'|'daily-limit'|'job-limit'|null}
 */
export function getApplyAriBlocker(user, opts = {}) {
  if (!user || !user.id) return null;
  const { briefNoPitchLimits = false, authUserWasInvited = false } = opts;
  if (briefNoPitchLimits || authUserWasInvited || user.premium) return null;
  if (user.pro && Number(user.messages_count) > 0) return null;
  if (!user.email_verified) return 'email-unverified';
  if (isApplyAriDailyLimitReached(user, opts)) return 'daily-limit';
  if (isApplyAriJobPitchLimitReached(user, opts)) return 'job-limit';
  return null;
}
